<!--
  Subject line performance.

  Every other marketing screen is one campaign at a time, so "does leading with the benefit actually beat leading
  with curiosity" was a question nobody could answer — the numbers existed, one send at a time, on a card you had
  to open a campaign to see. This is the sweep across all of them.

  It reads on the facets Subject.LineCritic derives from the subject text itself, so it covers sends that went out
  long before any of this shipped. The one exception is the angle, which is captured when the line is written and
  shows as Unknown on older sends.

  Ranked on CLICK rate, not opens, and that ordering is deliberate — see the caveat banner. It is a place to find
  a hypothesis worth testing, never proof: the copy, the audience and the send date all moved with the subject.
-->
<template>
  <v-container fluid>
    <v-card class="my-4">
      <v-card-title>
        Subject lines
        <v-spacer />
        <v-btn
          icon
          title="Refresh"
          :loading="loading"
          @click="load"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle>
        Which kinds of subject line have actually earned clicks, across every marketing email we've sent.
      </v-card-subtitle>

      <v-card-text>
        <v-btn-toggle
          v-model="days"
          mandatory
          dense
          class="mb-4"
          @change="load"
        >
          <v-btn :value="90">
            90 days
          </v-btn>
          <v-btn :value="180">
            6 months
          </v-btn>
          <v-btn :value="365">
            1 year
          </v-btn>
        </v-btn-toggle>

        <!-- Stated once, up front, rather than as a footnote per table: reading these as causal is the single
             way this screen can do damage. -->
        <v-alert
          type="info"
          text
          dense
          class="text-body-2"
        >
          These are correlations, not experiments — the copy, hero image, audience and send date all moved along
          with the subject line. <strong>Click rate is the metric to rank on.</strong> Open rate is inflated and
          randomized by Apple Mail Privacy Protection, which pre-fetches images for an unknowable share of
          recipients, so it is shown as directional only. Hover any rate to see the counts behind it. Check the
          send count before believing any row.
        </v-alert>

        <v-alert
          v-if="report.error"
          type="error"
          text
          dense
        >
          {{ report.error }}
        </v-alert>

        <div
          v-else-if="!loading"
          class="text-caption grey--text"
        >
          {{ report.sendsAnalyzed }} send{{ report.sendsAnalyzed === 1 ? '' : 's' }} with reporting data.
          <span v-if="report.sendsWithoutData">
            {{ report.sendsWithoutData }} more sent in this window have no events stored yet, and are not counted.
          </span>
        </div>
      </v-card-text>
    </v-card>

    <v-skeleton-loader
      v-if="loading"
      type="article, article"
    />

    <template v-else-if="!report.error">
      <div
        v-if="!report.sendsAnalyzed"
        class="text-body-2 grey--text py-10 text-center"
      >
        No sends with reporting data in this window yet.
      </div>

      <template v-else>
        <v-row>
          <v-col
            v-for="group in facetGroups"
            :key="group.facet"
            cols="12"
            md="6"
          >
            <v-card class="mb-4">
              <v-card-title class="text-subtitle-1">
                {{ group.label }}
              </v-card-title>
              <v-card-subtitle class="text-caption">
                {{ group.hint }}
              </v-card-subtitle>
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ group.valueLabel }}
                      </th>
                      <th class="text-right">
                        Sends
                      </th>
                      <th class="text-right">
                        Click
                      </th>
                      <th class="text-right">
                        CTOR
                      </th>
                      <th class="text-right">
                        Conv
                      </th>
                      <th class="text-right grey--text">
                        Open
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in group.rows"
                      :key="row.value"
                      :class="{ 'thin-evidence': row.sends < report.minSendsForSignal }"
                    >
                      <td>
                        {{ row.value }}
                        <v-icon
                          v-if="row.sends < report.minSendsForSignal"
                          x-small
                          :title="`Only ${row.sends} sends — not enough to mean anything yet.`"
                        >
                          mdi-help-circle-outline
                        </v-icon>
                      </td>
                      <td class="text-right">
                        {{ row.sends }}
                      </td>
                      <td
                        class="text-right font-weight-medium"
                        :title="ratio(row.uniqueClicks, row.delivered, 'unique clicks', 'delivered')"
                      >
                        {{ percent(row.clickRate) }}
                      </td>
                      <td
                        class="text-right"
                        :title="ratio(row.uniqueClicks, row.uniqueOpens, 'unique clicks', 'unique opens')"
                      >
                        {{ percent(row.clickToOpenRate) }}
                      </td>
                      <td
                        class="text-right"
                        :title="rateOf(row.conversionRate, 'of delivered converted')"
                      >
                        {{ countOf(row.uniqueConversions, row.delivered) }}
                      </td>
                      <td
                        class="text-right grey--text"
                        :title="ratio(row.uniqueOpens, row.delivered, 'unique opens', 'delivered')"
                      >
                        {{ percent(row.openRate) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="mb-4">
          <v-card-title class="text-subtitle-1">
            Every send
          </v-card-title>
          <v-card-subtitle class="text-caption">
            Best click rate first. Open a row to see the campaign it went out with.
          </v-card-subtitle>
          <v-data-table
            :headers="sendHeaders"
            :items="report.sends"
            :items-per-page="25"
            dense
            @click:row="openCampaign"
          >
            <template #[`item.subject`]="{ item }">
              <div>{{ item.subject }}</div>
              <div class="text-caption grey--text">
                {{ item.campaignName }}
              </div>
            </template>
            <!-- Who it was aimed at, as the targeting was written. A campaign with no audience-shaped subject
                 went to everybody, which is a real state rather than missing data — so it says so. -->
            <template #[`item.audiences`]="{ item }">
              <div
                v-if="item.audiences && item.audiences.length"
                class="audience-cell"
              >
                <div
                  v-for="audience in item.audiences"
                  :key="audience"
                >
                  <span
                    v-if="audience.includes(' > ')"
                    class="grey--text"
                  >{{ audience.split(' > ')[0] }} ›
                  </span>{{ audience.includes(' > ') ? audience.split(' > ')[1] : audience }}
                </div>
              </div>
              <span
                v-else
                class="grey--text font-italic"
              >Everyone</span>
            </template>
            <template #[`item.sentDate`]="{ item }">
              {{ item.sentDate ? new Date(item.sentDate).toLocaleDateString() : '' }}
            </template>
            <template #[`item.clickRate`]="{ item }">
              <span :title="ratio(item.uniqueClicks, item.delivered, 'unique clicks', 'delivered')">
                {{ percent(item.clickRate) }}
              </span>
            </template>
            <template #[`item.clickToOpenRate`]="{ item }">
              <span :title="ratio(item.uniqueClicks, item.uniqueOpens, 'unique clicks', 'unique opens')">
                {{ percent(item.clickToOpenRate) }}
              </span>
            </template>
            <template #[`item.conversionRate`]="{ item }">
              <span :title="rateOf(item.conversionRate, 'of delivered converted')">
                {{ countOf(item.uniqueConversions, item.delivered) }}
              </span>
            </template>
            <template #[`item.openRate`]="{ item }">
              <span
                class="grey--text"
                :title="ratio(item.uniqueOpens, item.delivered, 'unique opens', 'delivered')"
              >{{ percent(item.openRate) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </template>
  </v-container>
</template>

<script>
export default {
  name: 'SubjectPerformance',
  data: () => ({
    loading: false,
    days: 365,
    report: { facets: [], sends: [], sendsAnalyzed: 0, sendsWithoutData: 0, minSendsForSignal: 3 },
    // Click rate leads and open rate trails deliberately, so the honest metric is the one read first.
    sendHeaders: [
      { text: 'Subject', value: 'subject' },
      // Not sortable: the value is a list, so Vuetify would sort on "[object Object]" and look broken.
      { text: 'Audience', value: 'audiences', sortable: false },
      { text: 'Angle', value: 'angle' },
      { text: 'Links', value: 'links', align: 'end' },
      { text: 'Images', value: 'images', align: 'end' },
      { text: 'Sent', value: 'sentDate', align: 'end' },
      { text: 'Delivered', value: 'delivered', align: 'end' },
      { text: 'Click', value: 'clickRate', align: 'end' },
      { text: 'CTOR', value: 'clickToOpenRate', align: 'end' },
      { text: 'Conv', value: 'conversionRate', align: 'end' },
      { text: 'Open', value: 'openRate', align: 'end' }
    ],
    // How each facet family is presented. Keyed by the facet names Subject.LineCritic emits; anything it starts
    // emitting that isn't listed here still renders, under its own raw name.
    facetLabels: {
      angle: {
        label: 'Angle',
        valueLabel: 'Angle',
        hint: 'What the line leads with. Captured when the line is written, so older sends show as Unknown.'
      },
      length: {
        label: 'Subject length',
        valueLabel: 'Characters',
        hint: 'Visible characters, not counting a leading emoji. Phones show about 35.'
      },
      words: { label: 'Word count', valueLabel: 'Words', hint: 'How many words the line runs to.' },
      emoji: { label: 'Emoji', valueLabel: 'Has emoji', hint: 'Whether the line carries an emoji at all.' },
      question: { label: 'Question', valueLabel: 'Ends in "?"', hint: 'Whether the line is phrased as a question.' },
      numeral: { label: 'Numerals', valueLabel: 'Has a number', hint: 'Whether the line contains a digit.' },
      // Not about the subject line — these describe the body. Counted off the stored HTML, so history has them too.
      links: {
        label: 'Links in the email',
        valueLabel: 'Links',
        hint: 'Clickable links in the body, repeats included. Unsubscribe and other Customer.io links not counted.'
      },
      images: { label: 'Images in the email', valueLabel: 'Images', hint: 'Images in the body, hero included.' }
    }
  }),
  computed: {
    // The flat facet rows, grouped back into one card per family. Server order is preserved inside each group
    // (facet, then descending click rate), so the leading value is always the top row.
    facetGroups() {
      const groups = [];
      (this.report.facets || []).forEach((row) => {
        let group = groups.find((g) => g.facet === row.facet);
        if (!group) {
          const labels = this.facetLabels[row.facet] || {};
          group = {
            facet: row.facet,
            label: labels.label || row.facet,
            valueLabel: labels.valueLabel || 'Value',
            hint: labels.hint || '',
            rows: []
          };
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups;
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.$store
        .dispatch('campaigns/getSubjectPerformance', this.days)
        .then((report) => {
          this.report = report || this.report;
        })
        .catch(() => {
          this.$root.$snackbarError('Could not load subject performance.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCampaign(item) {
      this.$router.push(`/marketing/campaigns/${item.campaignId}`);
    },
    // An em dash rather than 0% when there is no denominator — a send with no delivered events did not have a
    // zero click rate, it has no click rate.
    percent(rate) {
      return rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(1)}%`;
    },
    // Every rate here is a ratio the row already carries both halves of, so hovering states it outright rather
    // than leaving anyone to reconstruct it from a percentage — which matters most on the thin columns, where
    // 0.1% could be four conversions or four hundred.
    ratio(count, total, noun, denominatorNoun) {
      if (count === null || count === undefined || !total) return 'No data for this row yet.';
      return `${count.toLocaleString()} ${noun} of ${total.toLocaleString()} ${denominatorNoun}`;
    },
    // Conversion is rare enough that its rate reads 0.0% on nearly every row, which hides the difference between
    // "none of 40" and "none of 4,000". So that column states the count outright and keeps the rate for the hover.
    countOf(count, total) {
      if (!total) return '—';
      return `${(count || 0).toLocaleString()} of ${total.toLocaleString()}`;
    },
    rateOf(rate, suffix) {
      return rate === null || rate === undefined ? 'No data for this row yet.' : `${this.percent(rate)} ${suffix}`;
    }
  }
};
</script>

<style scoped>
/* The industry reads as the parent of the topic, not as equal weight to it — a campaign row scans as its
   audience first, and the topic is the part that distinguishes one row from the next. */
.audience-cell {
  font-size: 12px;
  line-height: 1.35;
  white-space: nowrap;
}
/* Rows the send count says not to trust yet. Dimmed rather than hidden: "we don't know about this one" is
   itself worth seeing, and hiding them would make the table look more conclusive than it is. */
.thin-evidence {
  opacity: 0.5;
}

/* Every rate carries its raw counts in a title. Without a cursor cue nobody discovers a tooltip they weren't
   told about, and the counts are the thing that makes a thin column readable. */
td[title],
span[title] {
  cursor: help;
}
</style>
